// previewType maps a package's real classifiers (category, document kind) onto one
// of the six IBCS schematic chart types the <ibcs-preview> component can draw. The
// registry has no dedicated "chart type" field, so the thumbnail is derived: a
// recognised category (the design's "CATEGORIES" — Variance, Waterfall, P&L, …)
// wins; otherwise the document kind picks a sensible schematic; otherwise a column.

export type PreviewType = "waterfall" | "variance" | "column" | "line" | "integrated" | "table";

const PREVIEW_TYPES = new Set<PreviewType>([
  "waterfall",
  "variance",
  "column",
  "line",
  "integrated",
  "table",
]);

// Category label (lowercased) → schematic. Covers the design's sample categories.
const CATEGORY_MAP: Record<string, PreviewType> = {
  waterfall: "waterfall",
  bridge: "waterfall",
  "cash flow": "waterfall",
  cashflow: "waterfall",
  variance: "variance",
  deviation: "variance",
  sales: "variance",
  column: "column",
  structure: "column",
  kpi: "column",
  cost: "column",
  line: "line",
  "time series": "line",
  timeseries: "line",
  forecast: "line",
  trend: "line",
  integrated: "integrated",
  comparison: "integrated",
  table: "table",
  "p&l": "table",
  pl: "table",
  "p&l statement": "table",
  grid: "table",
};

// Document kind → schematic (fallback when the category is unknown/empty).
const KIND_MAP: Record<string, PreviewType> = {
  LayoutPage: "table",
  LayoutCard: "column",
  Text: "table",
  Table: "table",
  ChartStructure: "waterfall",
  ChartTime: "line",
  ChartScatter: "line",
  ChartBubble: "line",
  ChartBullet: "integrated",
  Tree: "integrated",
  Grid: "table",
  Asset: "column",
  ComponentStyle: "variance",
  Internationalization: "table",
  ScalingGroup: "column",
  DataSet: "table",
  DataSource: "table",
};

/** previewType derives an IBCS schematic from a package's kind and category. */
export function previewType(kind?: string | null, category?: string | null): PreviewType {
  const cat = (category ?? "").trim().toLowerCase();
  if (cat) {
    if (PREVIEW_TYPES.has(cat as PreviewType)) return cat as PreviewType;
    if (CATEGORY_MAP[cat]) return CATEGORY_MAP[cat];
  }
  return KIND_MAP[kind ?? ""] ?? "column";
}
